import { adminPath, useScope } from "../lib/scope";
import { ChevronDownIcon, ChevronRightIcon, FileCodeIcon, GearIcon } from "@primer/octicons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, HttpError } from "../api/client";
import { useAction, useRunners } from "../api/hooks";
import type { PlacementPlatform, RunnerPlacement, RunnerPlatform, RunnerSetup, RunnersView } from "../api/types";
import { CopyButton, ErrorMessage, Pre } from "../components/common";
import { Button } from "../components/ui/button";
import { CheckboxField } from "../components/ui/checkbox";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Field, FieldGrid, FieldSection } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { cn } from "../lib/utils";

export const PLATFORM_LABELS: Record<RunnerPlatform, string> = {
  kind: "Local (kind)",
  gcp: "Google Cloud",
  aws: "AWS",
  cloudflare: "Cloudflare",
  ubicloud: "Ubicloud",
};

// platformLabel names any platform a runner reports, including ones the UI has no label for.
export const platformLabel = (p: string) => PLATFORM_LABELS[p as RunnerPlatform] ?? p;

/** Platforms Bee can generate deploy commands and manifests for. */
const DEPLOYABLE: PlacementPlatform[] = ["kind", "gcp", "cloudflare"];
export const deployable = (p: string): p is PlacementPlatform => (DEPLOYABLE as string[]).includes(p);

type Key = Exclude<keyof RunnerPlacement, "platform" | "strict" | "spot">;
type FieldSpec = { key: Key; label: string; hint: string; mono?: boolean; number?: { max: number }; required?: boolean };
type Sections = { location: FieldSpec[]; connection: FieldSpec[]; capacity: FieldSpec[] };

const f = {
  server_url: { key: "server_url", label: "Server URL", hint: "How runners reach bee-server.", mono: true },
  image: { key: "image", label: "Runner image", hint: "Built from the Dockerfile's runner target.", mono: true },
  replicas: { key: "replicas", label: "Runners", hint: "Pods or container instances, 1–100.", number: { max: 100 } },
  concurrency: { key: "concurrency", label: "Jobs per runner", hint: "Parallel CI jobs each runner takes, 1–32.", number: { max: 32 } },
  cpu: { key: "cpu", label: "CPU request", hint: "Cores (2) or millicores (500m).", mono: true },
  memory: { key: "memory", label: "Memory request", hint: "Like 512Mi or 8Gi.", mono: true },
  cluster: { key: "cluster", label: "Cluster", hint: "Cluster name.", mono: true },
  namespace: { key: "namespace", label: "Namespace", hint: "Kubernetes namespace for the runners.", mono: true },
  region: { key: "region", label: "Region", hint: "Where the cluster runs.", mono: true },
} satisfies Record<string, FieldSpec>;

const k8sCapacity = [f.replicas, f.concurrency, f.cpu, f.memory];

/** Required flags mirror Placement.Validate in internal/server/modules/runners. */
const FIELDS: Record<PlacementPlatform, Sections> = {
  kind: {
    location: [{ ...f.cluster, hint: "kind cluster name (KIND_CLUSTER)." }, f.namespace],
    connection: [f.server_url, f.image],
    capacity: k8sCapacity,
  },
  gcp: {
    location: [
      { key: "project", label: "Project", hint: "GCP project id (Terraform -var project).", mono: true, required: true },
      f.region,
      { ...f.cluster, hint: "GKE cluster from deploy/terraform." },
      f.namespace,
    ],
    connection: [f.server_url, f.image],
    capacity: k8sCapacity,
  },
  cloudflare: {
    location: [{ key: "account", label: "Account ID", hint: "32-character Cloudflare account id.", mono: true, required: true }],
    connection: [
      { ...f.server_url, hint: "bee's public https URL.", required: true },
      { ...f.image, hint: "Pushed with wrangler containers push." },
    ],
    capacity: [f.replicas, f.concurrency],
  },
};

/** Splits a validation error's "field: message" issues by field. */
function fieldIssues(error: unknown): { byField: Partial<Record<string, string>>; rest: boolean } {
  const issues = error instanceof HttpError ? (error.body.detail as { issues?: unknown } | undefined)?.issues : undefined;
  if (!Array.isArray(issues)) return { byField: {}, rest: !!error };
  const byField: Partial<Record<string, string>> = {};
  let rest = false;
  for (const issue of issues) {
    const [field, ...msg] = String(issue).split(": ");
    if (field && msg.length && field !== "platform") byField[field] ??= msg.join(": ");
    else rest = true;
  }
  return { byField, rest };
}

type Draft = Partial<RunnerPlacement> & { platform: PlacementPlatform };

/** Empty fields are left out so the server fills its platform defaults. */
function body(d: Draft): Draft {
  return Object.fromEntries(Object.entries(d).filter(([, v]) => v !== "" && v !== 0 && v !== undefined)) as Draft;
}

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Where on its platform runners are deployed, in a few words. */
function whereLabel(p: RunnerPlacement): string {
  switch (p.platform) {
    case "kind":
      return `cluster ${p.cluster} · namespace ${p.namespace}`;
    case "gcp":
      return `${p.project} · ${p.region} · cluster ${p.cluster}`;
    case "cloudflare":
      return `account ${p.account?.slice(0, 8)}… · ${p.instance_type}`;
  }
}

/**
 * How to deploy runners on a platform: the commands and manifests, generated from its settings.
 * The settings (stored server-side as the platform's placement) stay folded until needed.
 */
export function Deployment({ platform }: { platform: PlacementPlatform }) {
  const q = useRunners();
  if (!q.data) return q.error ? <ErrorMessage error={q.error} /> : null;
  return <DeployForm key={platform} v={q.data} platform={platform} />;
}

function DeployForm({ v, platform }: { v: RunnersView; platform: PlacementPlatform }) {
  const org = useScope();
  const stored = v.placements.find((s) => s.placement.platform === platform);
  const saved = stored?.placement;
  const sections = FIELDS[platform];
  const [draft, setDraft] = useState<Draft>(() => ({ ...(saved ?? {}), platform }));
  const [open, setOpen] = useState(() => !saved && [...sections.location, ...sections.connection].some((fd) => fd.required));
  const [confirm, setConfirm] = useState(false);
  const save = useAction();
  const remove = useAction();
  const set = (patch: Partial<RunnerPlacement>) => {
    setDraft({ ...draft, ...patch });
    save.reset();
  };

  const wanted = useDebounced(body(draft), 300);
  const preview = useQuery({
    queryKey: ["runner-setup", wanted],
    queryFn: () => api.send<{ placement: RunnerPlacement; setup: RunnerSetup }>("POST", `${adminPath(org, "/runners")}/setup`, wanted),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const normalized = preview.isError || preview.isPlaceholderData ? undefined : preview.data?.placement;
  const dirty = !saved || !normalized || JSON.stringify(normalized) !== JSON.stringify(saved);
  /** Defaults for placeholders; the previous preview's stand in while the next one loads. */
  const defaults = preview.data?.placement.platform === platform ? preview.data.placement : undefined;

  if (stored?.placement.managed && !stored.setup.steps.length)
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        {platformLabel(platform)} runners are managed for every organization: nothing to deploy. Jobs run there unless runners of your own pick them up first.
      </p>
    );

  const invalid = preview.isError ? preview.error : save.error;
  const issues = fieldIssues(invalid);

  const input = (fd: FieldSpec) => {
    const error = issues.byField[fd.key];
    const fallback = defaults?.[fd.key];
    return (
      <Field key={fd.key} label={fd.label} required={fd.required} invalid={!!error} hint={error ? `${fd.label} ${error}.` : fd.hint}>
        <Input
          mono={fd.mono}
          type={fd.number ? "number" : "text"}
          inputMode={fd.number ? "numeric" : undefined}
          min={fd.number ? 1 : undefined}
          max={fd.number?.max}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={!!error}
          value={(draft[fd.key] as string | number | undefined) || ""}
          placeholder={fallback ? `${fallback} (default)` : fd.required ? "required" : ""}
          onChange={(e) => set({ [fd.key]: fd.number ? Number(e.target.value) : e.target.value.trim() })}
        />
      </Field>
    );
  };

  const replicas = draft.replicas || defaults?.replicas || 0;
  const concurrency = draft.concurrency || defaults?.concurrency || 0;
  const summary = normalized ? whereLabel(normalized) : defaults ? whereLabel(defaults) : "";

  return (
    <div className="mt-2">
      <div className="rounded-md border">
        <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
          <GearIcon className="text-muted-foreground" /> <b>Settings</b>
          <span className="min-w-0 flex-1 truncate font-mono text-muted-foreground">{summary}</span>
          {replicas > 0 && concurrency > 0 && (
            <span className="whitespace-nowrap text-muted-foreground">
              up to {replicas * concurrency} job{replicas * concurrency === 1 ? "" : "s"}
            </span>
          )}
        </button>
        {open && (
          <form
            noValidate
            className="border-t px-3 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ method: "PUT", path: `${adminPath(org, "/runners")}/placements/${platform}`, body: body(draft) });
            }}
          >
            <p className="mb-4 text-xs text-muted-foreground">Leave a field empty to use the default shown in it. The steps below follow the form.</p>
            <FieldSection title="Location">
              <FieldGrid className="mb-0">{sections.location.map(input)}</FieldGrid>
            </FieldSection>
            <FieldSection title="Connection">
              <FieldGrid className="mb-0">{sections.connection.map(input)}</FieldGrid>
            </FieldSection>
            <FieldSection title="Capacity">
              <FieldGrid className="mb-0">
                {sections.capacity.map(input)}
                {platform === "cloudflare" && (
                  <Field
                    label="Instance type"
                    invalid={!!issues.byField.instance_type}
                    hint={issues.byField.instance_type ? `Instance type ${issues.byField.instance_type}.` : "Container size for each runner."}
                  >
                    <Select
                      value={draft.instance_type ?? ""}
                      onValueChange={(t) => set({ instance_type: t })}
                      options={[
                        { value: "", label: `${defaults?.instance_type ?? "standard-2"} (default)` },
                        ...v.instance_types.map((t) => ({ value: t, label: t })),
                      ]}
                      aria-invalid={!!issues.byField.instance_type}
                    />
                  </Field>
                )}
              </FieldGrid>
              {platform === "gcp" && (
                <CheckboxField
                  className="mt-3"
                  checked={!!draft.spot}
                  onCheckedChange={(c) => set({ spot: c === true })}
                  title="Spot capacity"
                  description="Cheaper, preemptible nodes. A preempted runner misses its job deadline and the server retries the job once."
                />
              )}
            </FieldSection>
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn("min-w-0 flex-1 text-xs text-muted-foreground", invalid && "text-danger")}>
                {invalid
                  ? "Fix the highlighted fields."
                  : preview.isFetching && dirty
                    ? "Checking…"
                    : dirty
                      ? "Save to keep these settings for next time."
                      : "Saved."}
              </span>
              {saved && !saved.managed && (
                <Button type="button" variant="ghost" disabled={remove.isPending} onClick={() => setConfirm(true)}>
                  Reset to defaults
                </Button>
              )}
              <Button variant="primary" disabled={save.isPending || preview.isError || !dirty}>
                {save.isPending ? "Saving…" : "Save settings"}
              </Button>
            </div>
            {invalid && issues.rest && <ErrorMessage error={invalid} />}
            {remove.error ? <ErrorMessage error={remove.error} /> : null}
            <ConfirmDialog
              open={confirm}
              onOpenChange={setConfirm}
              title={`Forget the saved ${platformLabel(platform)} settings?`}
              description={
                v.strict && v.placements.length > 1
                  ? `Only runners on saved platforms take CI jobs, so runners on ${platformLabel(platform)} stop getting them. Runners already deployed keep running until you remove them there.`
                  : `Runners already deployed on ${platformLabel(platform)} keep running and taking CI jobs; this only forgets the saved settings.`
              }
              confirmLabel="Forget settings"
              onConfirm={() =>
                remove.mutate(
                  { method: "DELETE", path: `${adminPath(org, "/runners")}/placements/${platform}`, body: {} },
                  { onSuccess: () => setDraft({ platform }) },
                )
              }
            />
          </form>
        )}
      </div>

      {preview.data && !preview.isError && <SetupSteps setup={preview.data.setup} />}
    </div>
  );
}

function SetupSteps({ setup }: { setup: RunnerSetup }) {
  return (
    <>
      <ol className="mt-4 flex list-decimal flex-col gap-4 pl-5 text-xs">
        {setup.steps.map((s) => (
          <li key={s.title}>
            <b className="text-sm">{s.title}</b>
            {s.detail && <div className="text-muted-foreground">{s.detail}</div>}
            {s.command && <Snippet text={s.command} />}
          </li>
        ))}
      </ol>
      {setup.files.map((file) => (
        <div key={file.path} className="mt-4 text-xs">
          <div className="flex items-center gap-2">
            <FileCodeIcon className="text-muted-foreground" /> <b className="min-w-0 flex-1 font-mono">{file.path}</b>{" "}
            <CopyButton value={file.content} title={`Copy ${file.path}`} />
          </div>
          <Pre>{file.content}</Pre>
        </div>
      ))}
    </>
  );
}

function Snippet({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-1">
      <Pre className="min-w-0 flex-1 whitespace-pre-wrap break-all">{text}</Pre>
      <span className="mt-3">
        <CopyButton value={text} title="Copy command" />
      </span>
    </div>
  );
}
